import {
  pieceStitchLines,
  type ConstructionOutline,
  type Cutout,
  type Piece,
  type Project,
  type StitchLine,
} from "../Project";
import {
  bakeConstructionOutline,
  referencedLineIds,
  tracedLineIds,
} from "../constructionOutline";
import { isFeatureEnabled } from "../../featureFlags";
import traceChipRaw from "../../icons/chip-trace.svg?raw";
import { stripIconSvg } from "../../icons/stripIconSvg";
import type {
  Connection,
  ConnectionChipSpec,
  ConnectionRowSpec,
} from "./Connection";

/** Trace links are the Construction connections feature (#37), gated behind
 *  the `construction-connections` flag. When it's off the trace connection
 *  surfaces nothing — no part chip, no per-line chips/rows. */
function traceVisible(): boolean {
  return isFeatureEnabled("construction-connections");
}

/** The "trace" connection (#650): a leather piece whose outline was traced from
 *  construction lines is linked to those lines. Unlike snap / offset / mirror
 *  (which relate construction lines to each other), this relates a PIECE to its
 *  source lines — so the line-graph methods here are inert. It exists in the
 *  registry purely to surface chips + Connections rows on the construction-line
 *  side; the part side is rendered inline in the properties panel. */

const KIND = "trace" as const;

/** Pen-trace chip glyph (currentColor; CSS sizes it to the chip width). */
export const TRACE_CHIP_ICON_SVG = stripIconSvg(traceChipRaw);

/** One key PER TRACED PIECE, shared by every trace chip/row for that piece —
 *  the part chip in the layers panel, the "Traced by …" chips/rows on each
 *  source line, and the "Traces …" rows on the part. Hovering any one
 *  cross-highlights them all (so you can see which lines produced which part,
 *  and vice versa). */
export function traceConnectionKey(pieceId: string): string {
  return `trace:${pieceId}`;
}

/** A cut-out or seam traced from construction lines (#1202). They carry the
 *  same `constructionOutline` link a part's outline does — traced, filled, or
 *  promoted with Create construction line — so they get the same chip and
 *  rows, on their own row and on every source line. */
export type TracedSubKind = "cutout" | "stitch";

export interface TracedSub {
  kind: TracedSubKind;
  pieceId: string;
  id: string;
}

/** One key per traced cut-out / seam, distinct from its part's, so hovering a
 *  seam's chip lights up only the lines that seam follows. */
export function subTraceConnectionKey(sub: TracedSub): string {
  return `trace:${sub.pieceId}:${sub.kind}:${sub.id}`;
}

function subLineIds(entity: {
  constructionOutline?: ConstructionOutline;
}): string[] {
  return entity.constructionOutline
    ? [...referencedLineIds(entity.constructionOutline)]
    : [];
}

function piecesTracing(project: Project, lineId: string): Piece[] {
  return project.pieces.filter((p) => tracedLineIds(p).includes(lineId));
}

/** Every cut-out and seam tracing `lineId`, in project order. */
function subsTracing(project: Project, lineId: string): TracedSub[] {
  const out: TracedSub[] = [];
  for (const p of project.pieces) {
    for (const c of p.cutouts) {
      if (subLineIds(c).includes(lineId))
        out.push({ kind: "cutout", pieceId: p.id, id: c.id });
    }
    for (const l of pieceStitchLines(p)) {
      if (subLineIds(l).includes(lineId))
        out.push({ kind: "stitch", pieceId: p.id, id: l.id });
    }
  }
  return out;
}

function findSub(
  project: Project,
  sub: TracedSub,
): { piece: Piece; entity: Cutout | StitchLine; index: number } | null {
  const piece = project.pieces.find((p) => p.id === sub.pieceId);
  if (!piece) return null;
  const list: Array<Cutout | StitchLine> =
    sub.kind === "cutout" ? piece.cutouts : pieceStitchLines(piece);
  const index = list.findIndex((e) => e.id === sub.id);
  return index >= 0 ? { piece, entity: list[index]!, index } : null;
}

/** "Cutout 2 on Part 1" / "Stitch 3 on Part 1" — the part is named because a
 *  cut-out's default name only counts within its own part. */
function subLabel(project: Project, sub: TracedSub): string {
  const found = findSub(project, sub);
  if (!found) return sub.kind === "cutout" ? "a cutout" : "a stitch line";
  const own =
    found.entity.name ??
    `${sub.kind === "cutout" ? "Cutout" : "Stitch"} ${found.index + 1}`;
  return `${own} on ${pieceLabel(project, found.piece)}`;
}

function subClickTarget(sub: TracedSub) {
  return sub.kind === "cutout"
    ? { pieceId: sub.pieceId, cutoutId: sub.id }
    : { pieceId: sub.pieceId, stitchId: sub.id };
}

/** Commit that drops a cut-out's or seam's trace link, keeping its geometry. */
export function detachTracedSub(sub: TracedSub) {
  return (proj: Project): Project => ({
    ...proj,
    pieces: proj.pieces.map((pc) => {
      if (pc.id !== sub.pieceId) return pc;
      if (sub.kind === "cutout") {
        return {
          ...pc,
          cutouts: pc.cutouts.map((c) => {
            if (c.id !== sub.id || !c.constructionOutline) return c;
            const { constructionOutline: _drop, ...rest } = c;
            return rest;
          }),
        };
      }
      return {
        ...pc,
        stitchLines: pieceStitchLines(pc).map((l) => {
          if (l.id !== sub.id || !l.constructionOutline) return l;
          const { constructionOutline: _drop, ...rest } = l;
          return rest;
        }),
      };
    }),
  });
}

/** The trace chip on a traced cut-out's or seam's own Parts-list row (#1202),
 *  the counterpart of `partTraceChipSpec`. Null when it isn't traced. */
export function subTraceChipSpec(
  project: Project,
  sub: TracedSub,
): ConnectionChipSpec | null {
  if (!traceVisible()) return null;
  const found = findSub(project, sub);
  const lineIds = found ? subLineIds(found.entity) : [];
  if (lineIds.length === 0) return null;
  const names = lineIds.map((id) => lineLabel(project, id));
  return {
    kind: KIND,
    connectionKey: subTraceConnectionKey(sub),
    role: "traces",
    iconSvg: TRACE_CHIP_ICON_SVG,
    ariaLabel: `Traced from ${lineIds.length} construction line${lineIds.length === 1 ? "" : "s"}`,
    title: `Traces: ${names.join(", ")}`,
    className: "trace-chip",
    hoverRefs: lineIds.map((id) => ({ kind: "line", constructionId: id })),
    clickTarget: { constructionId: lineIds[0]! },
  };
}

/** The "Traces …" Connections rows in a traced cut-out's or seam's panel
 *  (#1202), one per source line, each detachable. */
export function subTraceRowSpecs(
  project: Project,
  sub: TracedSub,
): ConnectionRowSpec[] {
  if (!traceVisible()) return [];
  const found = findSub(project, sub);
  const lineIds = found ? subLineIds(found.entity) : [];
  const what = sub.kind === "cutout" ? "cutout" : "stitch line";
  return lineIds.map((lineId) => {
    const name = lineLabel(project, lineId);
    return {
      kind: KIND,
      connectionKey: subTraceConnectionKey(sub),
      role: "traces",
      iconSvg: TRACE_CHIP_ICON_SVG,
      ariaLabel: `Traces ${name}`,
      title: `Traces ${name}`,
      className: "trace-chip",
      hoverRefs: [{ kind: "line", constructionId: lineId }],
      clickTarget: { constructionId: lineId },
      label: `Traces ${name}`,
      onDetach: (editor) =>
        editor.commit(`Detach traced ${what}`, detachTracedSub(sub)),
    };
  });
}

function pieceLabel(project: Project, piece: Piece): string {
  return piece.name || `Part ${project.pieces.indexOf(piece) + 1}`;
}

function lineLabel(project: Project, lineId: string): string {
  const lines = project.constructionLines ?? [];
  const idx = lines.findIndex((l) => l.id === lineId);
  return lines[idx]?.name ?? `Construction ${idx + 1}`;
}

/** The trace chip shown on a traced part's layers-panel row — one chip
 *  representing the whole trace, hovering it highlights every source line's
 *  chip (and the lines on the canvas). Null when the piece isn't traced. */
export function partTraceChipSpec(
  project: Project,
  piece: Piece,
): ConnectionChipSpec | null {
  if (!traceVisible()) return null;
  const lineIds = tracedLineIds(piece);
  if (lineIds.length === 0) return null;
  const names = lineIds.map((id) => lineLabel(project, id));
  return {
    kind: KIND,
    connectionKey: traceConnectionKey(piece.id),
    role: "traces",
    iconSvg: TRACE_CHIP_ICON_SVG,
    ariaLabel: `Traced from ${lineIds.length} construction line${lineIds.length === 1 ? "" : "s"}`,
    title: `Traces: ${names.join(", ")}`,
    className: "trace-chip",
    hoverRefs: lineIds.map((id) => ({ kind: "line", constructionId: id })),
    // Click jumps to the (first) source construction line (#221).
    clickTarget: { constructionId: lineIds[0]! },
  };
}

/** Commit that drops a piece's trace link, keeping its last geometry. */
export function detachTracedPiece(pieceId: string) {
  return (proj: Project): Project => ({
    ...proj,
    pieces: proj.pieces.map((pc) =>
      pc.id === pieceId ? bakeConstructionOutline(pc) : pc,
    ),
  });
}

/** The part-side "Traces …" Connections rows shown on a traced piece — one per
 *  source construction line, the mirror of the line-side "Traced by …" rows
 *  (`rowsForLine`). Co-located with `partTraceChipSpec` so the row and chip
 *  can't drift: both carry the same `connectionKey`, hover refs, and — the
 *  reason this exists (#221) — a `clickTarget` that jumps to the source line.
 *  (The rows used to be hand-built in `piecePanelSection.ts`, which is why they
 *  silently lacked the click target the chip already had.) Empty when the
 *  feature is off or the piece isn't traced. */
export function partTraceRowSpecs(
  project: Project,
  piece: Piece,
): ConnectionRowSpec[] {
  if (!traceVisible()) return [];
  const lineIds = tracedLineIds(piece);
  if (lineIds.length === 0) return [];
  const pieceId = piece.id;
  return lineIds.map((lineId) => {
    const name = lineLabel(project, lineId);
    return {
      kind: KIND,
      connectionKey: traceConnectionKey(piece.id),
      role: "traces",
      iconSvg: TRACE_CHIP_ICON_SVG,
      ariaLabel: `Traces ${name}`,
      title: `Traces ${name}`,
      className: "trace-chip",
      hoverRefs: [{ kind: "line", constructionId: lineId }],
      // Click jumps to the source construction line (#221) — same target the
      // part chip uses.
      clickTarget: { constructionId: lineId },
      label: `Traces ${name}`,
      onDetach: (editor) =>
        editor.commit("Detach traced part", detachTracedPiece(pieceId)),
    };
  });
}

export const traceConnection: Connection = {
  kind: KIND,
  // Display order only — trace has no commit-time derive (piece outlines are
  // re-derived by `deriveConstructionOutlines`, not here).
  order: 3,

  chipsForLine(line, project) {
    if (!traceVisible()) return [];
    const subChips: ConnectionChipSpec[] = subsTracing(project, line.id).map(
      (sub) => {
        const name = subLabel(project, sub);
        return {
          kind: KIND,
          connectionKey: subTraceConnectionKey(sub),
          role: "traced-by",
          iconSvg: TRACE_CHIP_ICON_SVG,
          ariaLabel: `Traced by ${name}`,
          title: `Traced by ${name}`,
          className: "trace-chip",
          hoverRefs: [{ kind: "line", constructionId: line.id }],
          clickTarget: subClickTarget(sub),
        };
      },
    );
    const partChips: ConnectionChipSpec[] = piecesTracing(project, line.id).map(
      (p) => ({
        kind: KIND,
        connectionKey: traceConnectionKey(p.id),
        role: "traced-by",
        iconSvg: TRACE_CHIP_ICON_SVG,
        ariaLabel: `Traced by ${pieceLabel(project, p)}`,
        title: `Traced by ${pieceLabel(project, p)}`,
        className: "trace-chip",
        hoverRefs: [{ kind: "line", constructionId: line.id }],
        clickTarget: { pieceId: p.id },
      }),
    );
    return [...partChips, ...subChips];
  },

  rowsForLine(line, project): ConnectionRowSpec[] {
    if (!traceVisible()) return [];
    const subRows: ConnectionRowSpec[] = subsTracing(project, line.id).map(
      (sub) => {
        const name = subLabel(project, sub);
        const what = sub.kind === "cutout" ? "cutout" : "stitch line";
        return {
          kind: KIND,
          connectionKey: subTraceConnectionKey(sub),
          role: "traced-by",
          iconSvg: TRACE_CHIP_ICON_SVG,
          ariaLabel: `Traced by ${name}`,
          title: `Traced by ${name}`,
          className: "trace-chip",
          hoverRefs: [{ kind: "line", constructionId: line.id }],
          clickTarget: subClickTarget(sub),
          label: `Traced by ${name}`,
          onDetach: (editor) =>
            editor.commit(`Detach traced ${what}`, detachTracedSub(sub)),
        };
      },
    );
    const partRows: ConnectionRowSpec[] = piecesTracing(project, line.id).map(
      (p) => {
        const name = pieceLabel(project, p);
        const pieceId = p.id;
        return {
          kind: KIND,
          connectionKey: traceConnectionKey(p.id),
          role: "traced-by",
          iconSvg: TRACE_CHIP_ICON_SVG,
          ariaLabel: `Traced by ${name}`,
          title: `Traced by ${name}`,
          className: "trace-chip",
          hoverRefs: [{ kind: "line", constructionId: line.id }],
          clickTarget: { pieceId },
          label: `Traced by ${name}`,
          onDetach: (editor) =>
            editor.commit("Detach traced part", detachTracedPiece(pieceId)),
        };
      },
    );
    return [...partRows, ...subRows];
  },

  // Inert line-graph methods: trace links pieces to lines, not lines to lines.
  // A deleted source line auto-bakes its dependent pieces in
  // `deriveConstructionOutlines`, so there's nothing to prune here.
  pruneForDeletedLines: ({ project }) => project,
  forwardClosure: () => new Set<string>(),
  resolveRoot: (_project, lineId) => lineId,
  isDerived: () => false,
};
